import { useRef, useState, useEffect, useCallback, useLayoutEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Play, MessagesSquare, ChevronLeft, ChevronRight } from 'lucide-react';
import type { TFunction } from 'i18next';
import { TranslatedCalling } from './common';
import {
  normalizeTownSquareCommunityPostPath,
  parseCommunityDiscussionToolOutput,
  plainTextSnippetFromHtml,
} from '@arthelper/shared';
import { useAnalytics } from '~/context/analytics';
import { getPublicSiteBaseUrl } from '~/utils/index';

function postHref(pathOrUrl: string) {
  const base = getPublicSiteBaseUrl().replace(/\/$/, '');
  if (pathOrUrl.startsWith('http')) {
    try {
      const u = new URL(pathOrUrl);
      u.pathname = normalizeTownSquareCommunityPostPath(u.pathname);
      return u.toString();
    } catch {
      return pathOrUrl;
    }
  }
  const path = normalizeTownSquareCommunityPostPath(pathOrUrl.startsWith('/') ? pathOrUrl : `/${pathOrUrl}`);
  return `${base}${path.startsWith('/') ? '' : '/'}${path}`;
}

function isYoutubeThumbnail(url: string) {
  return url.includes('img.youtube.com');
}

/** Matches `gap-3` on the flex row (town-square carousels use 12px gap). */
const CARD_GAP_PX = 12;

function DiscussionCard({ item }: { item: any }) {
  const { amplitude } = useAnalytics();
  const thumb = typeof item.thumbnailUrl === 'string' ? item.thumbnailUrl : null;
  // Strip any raw HTML from snippet (handles old cached results that pre-date API-side stripping).
  // Do NOT fall back to item.snippet — if stripping yields nothing, show nothing.
  const snippetText = plainTextSnippetFromHtml(item.snippet) ?? null;

  const titleRef = useRef<HTMLDivElement>(null);
  const [titleWrapped, setTitleWrapped] = useState(false);

  useLayoutEffect(() => {
    const el = titleRef.current;
    if (!el) return;
    const lineHeight = parseFloat(getComputedStyle(el).lineHeight) || 18;
    setTitleWrapped(el.scrollHeight > lineHeight + 4);
  });

  const cardHref = postHref(item.url);
  const postSlug = String(item.postSlug ?? item.id ?? '');

  return (
    <a
      data-discussion-carousel-card
      href={cardHref}
      target="_blank"
      rel="noopener noreferrer"
      style={{ scrollSnapAlign: 'start' }}
      className="my-1.5 flex h-24 w-[18rem] shrink-0 flex-row items-stretch overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm transition-all duration-200 hover:border-gray-300 hover:shadow-md hover:-translate-y-0.5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-500"
      onClick={() =>
        amplitude.track('chat_community_discussion_card_clicked', {
          post_id: String(item.id ?? ''),
          community_slug: String(item.communitySlug ?? ''),
          post_slug: postSlug,
          url: cardHref,
        })
      }
    >
      {/* w-20 self-stretch: fills card height edge-to-edge, width fixed at 80px. */}
      <div className="pointer-events-none relative z-0 w-20 shrink-0 self-stretch overflow-hidden rounded-l-xl border-r border-gray-100 bg-gray-100">
        {thumb ? (
          <>
            <img src={thumb} alt="" className="block h-full w-full object-cover object-center" loading="lazy" />
            {isYoutubeThumbnail(thumb) ? (
              <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/20">
                <div className="rounded-full bg-black/55 p-1.5">
                  <Play className="h-3 w-3 text-white fill-white" aria-hidden />
                </div>
              </div>
            ) : null}
          </>
        ) : (
          <div className="flex h-full w-full items-center justify-center text-gray-400">
            <MessagesSquare className="h-7 w-7 opacity-50" aria-hidden />
          </div>
        )}
      </div>

      <div className="relative flex min-w-0 flex-1 flex-col gap-1 rounded-r-xl bg-white py-2 pl-2.5 pr-2.5">
        <div className="text-[10px] uppercase tracking-wide text-gray-500 font-medium truncate">
          {item.communityName}
        </div>
        <div ref={titleRef} className="font-semibold text-[12px] text-gray-900 leading-snug line-clamp-2">
          {item.title}
        </div>
        {snippetText ? (
          <div className={`text-gray-500 text-[11px] leading-snug ${titleWrapped ? 'line-clamp-1' : 'line-clamp-2'}`}>
            {snippetText}
          </div>
        ) : null}
      </div>
    </a>
  );
}

function DiscussionCardsCarousel({ results, t }: { results: any[]; t: TFunction<'chat'> }) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 1);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 1);
  }, []);

  const getScrollStep = useCallback(() => {
    const root = scrollRef.current;
    if (!root) return 300;
    const first = root.querySelector<HTMLElement>('[data-discussion-carousel-card]');
    if (!first) return 300;
    return first.getBoundingClientRect().width + CARD_GAP_PX;
  }, []);

  const scrollPrev = () => {
    scrollRef.current?.scrollBy({ left: -getScrollStep(), behavior: 'smooth' });
  };

  const scrollNext = () => {
    scrollRef.current?.scrollBy({ left: getScrollStep(), behavior: 'smooth' });
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    updateScrollState();
    el.addEventListener('scroll', updateScrollState, { passive: true });
    const observer = new ResizeObserver(updateScrollState);
    observer.observe(el);
    return () => {
      el.removeEventListener('scroll', updateScrollState);
      observer.disconnect();
    };
  }, [updateScrollState, results.length]);

  useEffect(() => {
    const id = requestAnimationFrame(() => updateScrollState());
    return () => cancelAnimationFrame(id);
  }, [results.length, updateScrollState]);

  return (
    <div className="relative isolate overflow-visible pt-1">
      {/* Match town-square PopularCommunitiesCarousel: chevrons sit above the scroll track (z-20 vs z-0). */}
      <button
        type="button"
        onClick={scrollPrev}
        aria-label={t('tool.search-community-discussions.scroll-left')}
        className={`
          hidden md:flex absolute left-0 top-1/2 z-20 -translate-y-1/2 -translate-x-5
          w-9 h-9 items-center justify-center
          bg-white/70 backdrop-blur-md border border-white/40 rounded-full shadow-md
          text-gray-700 hover:text-gray-900 hover:bg-white/90 hover:shadow-lg
          transition-all duration-150
          ${canScrollLeft ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}
        `}
      >
        <ChevronLeft className="w-5 h-5" />
      </button>

      <div
        ref={scrollRef}
        className="relative z-0 -mr-1 overflow-x-auto py-3 pr-1 hide-scrollbar"
        style={{ scrollSnapType: 'x mandatory' }}
      >
        <div className="flex gap-3">
          {results.map((item: any, index: number) => (
            <DiscussionCard key={item.id ?? index} item={item} />
          ))}
        </div>
      </div>

      <button
        type="button"
        onClick={scrollNext}
        aria-label={t('tool.search-community-discussions.scroll-right')}
        className={`
          hidden md:flex absolute right-0 top-1/2 z-20 -translate-y-1/2 translate-x-5
          w-9 h-9 items-center justify-center
          bg-white/70 backdrop-blur-md border border-white/40 rounded-full shadow-md
          text-gray-700 hover:text-gray-900 hover:bg-white/90 hover:shadow-lg
          transition-all duration-150
          ${canScrollRight ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}
        `}
      >
        <ChevronRight className="w-5 h-5" />
      </button>
    </div>
  );
}

function SearchCommunityDiscussionsResult({ toolCall }: { toolCall: any }) {
  const { t } = useTranslation('chat');
  const result = toolCall.result || {};
  const { hasResults, results } = parseCommunityDiscussionToolOutput(result);

  if (!hasResults) {
    return null;
  }

  return (
    <div>
      <DiscussionCardsCarousel results={results} t={t} />
    </div>
  );
}

export const search_community_discussions = {
  type: 'acting',
  call: () => <TranslatedCalling i18nKey="tool.search-community-discussions.searching" />,
  'partial-call': () => <TranslatedCalling i18nKey="tool.search-community-discussions.searching-short" />,
  result: ({ toolCall }: any) => <SearchCommunityDiscussionsResult toolCall={toolCall} />,
};
